import React, { useEffect, useRef, useState } from "react";
import classes from "./AddUserInfos.module.css";
import { useServiceErstellen } from "../../../store/serviceErstellenStore";
import { useSelector } from "react-redux";
import { SmallModal } from "../../../components/UI/SmallModal/SmallModal";
import AvatarEditor from 'react-avatar-editor';
import ErrorMessage from "../../../components/StudioErstellen/ErrorMessage";
import noUserImage from "../../../public/fakeUser/noUser.webp";
import { notifyErrorToast, notifyValidationToast } from '../../../util/notifyActionError';

const ALLOWED_PROFILE_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/x-png']);
const ALLOWED_PROFILE_IMAGE_EXT = /\.(jpe?g|png)$/i;

function isAllowedProfileImageFile(file) {
    if (!file) return false;
    const fileType = (file.type || '').toLowerCase();
    if (fileType && ALLOWED_PROFILE_IMAGE_TYPES.has(fileType)) return true;
    return Boolean(file.name && ALLOWED_PROFILE_IMAGE_EXT.test(file.name));
}

export const AddUserInfos = ({ validate }) => {
    const { serviceData, updateServiceData } = useServiceErstellen();
    const user = useSelector((state) => state.auth.user);
    const fileInputRef = useRef(null);
    const editorRef = useRef(null);
    const editorContainerRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [formData, setFormData] = useState({
        profilePicture: null
    });
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
    const [displayNameError, setDisplayNameError] = useState('');
    const [errors, setErrors] = useState({
        displayName: '',
        jobTitle: '',
        profilePicture: ''
    });
    const gestureStateRef = useRef({
        isPanning: false,
        lastPoint: null,
        pinchStartDistance: null,
        startScale: 1,
        startPosition: { x: 0.5, y: 0.5 }
    });

    // Get the display profile picture (from serviceData or user)
    const displayProfilePicture = serviceData?.profilePicture?.url || user?.profilePicture;

    const validateDisplayName = (value) => {
        // Only letters, spaces, hyphens, apostrophes, and dots (no numbers or other symbols)
        if (value && !/^[a-zA-ZäöüÄÖÜß\s'.-]+$/.test(value)) {
            return 'Nur Buchstaben erlaubt';
        }
        return '';
    };

    const handleValidation = () => {
        let isValid = true;
        const newErrors = {
            displayName: '',
            jobTitle: '',
            profilePicture: ''
        };

        // Validate displayName - check serviceData first, then fallback to user's name only if not explicitly set
        // If serviceData.displayName is null/undefined, use fallback. If it's an empty string, user cleared it - show error.
        const displayNameValue = serviceData?.displayName;
        const displayName = displayNameValue !== null && displayNameValue !== undefined
            ? displayNameValue.trim()  // Use the value from serviceData (even if empty)
            : (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}`.trim() : '') ||
              user?.displayName?.trim() || '';  // Fallback only if not explicitly set
        
        if (!displayName) {
            newErrors.displayName = "Du musst einen Namen eingeben.";
            isValid = false;
        } else {
            // Validate format: only letters, spaces, hyphens, apostrophes, and dots (no numbers or other symbols)
            const formatError = validateDisplayName(displayName);
            if (formatError) {
                newErrors.displayName = formatError;
                isValid = false;
            } else if (displayNameError) {
                // If there's a format error from user input, use that
                newErrors.displayName = displayNameError;
                isValid = false;
            }
        }

        // Validate jobTitle
        if (!serviceData?.jobTitle?.trim()) {
            newErrors.jobTitle = "Du musst einen Jobtitel eingeben.";
            isValid = false;
        }

        // Validate profilePicture - accept either file (newly selected) or url (existing user image)
        const hasProfilePicture = 
            (serviceData?.profilePicture?.file) || 
            (serviceData?.profilePicture?.url) || 
            (user?.profilePicture);
        
        if (!hasProfilePicture) {
            newErrors.profilePicture = "Du musst ein Profilbild auswählen.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // This validation function will be called by the parent component
    useEffect(() => {
        validate.current = handleValidation;
    }, [serviceData?.displayName, serviceData?.jobTitle, serviceData?.profilePicture, displayNameError, user, validate]);

    // Helpers for editor interactions
    const EDITOR_SIZE = 200;
    const PAN_DAMPING = 1.6;
    const PINCH_EXPONENT = 0.65;
    const WHEEL_STEP_IN = 1.02;
    const WHEEL_STEP_OUT = 0.98;
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    const clampPosition = (pos) => ({ x: clamp(pos.x, 0, 1), y: clamp(pos.y, 0, 1) });

    const handleWheel = (e) => {
        const delta = -e.deltaY;
        const factor = delta > 0 ? WHEEL_STEP_IN : WHEEL_STEP_OUT;
        setScale(prev => clamp(prev * factor, 1, 3));
    };

    const distanceBetween = (t1, t2) => {
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        return Math.hypot(dx, dy);
    };

    const midpoint = (t1, t2) => ({
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2
    });

    const handleTouchStart = (e) => {
        const state = gestureStateRef.current;
        const container = editorContainerRef.current;
        if (!container) return;

        if (e.touches.length === 1) {
            state.isPanning = true;
            state.lastPoint = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            state.startPosition = position;
        } else if (e.touches.length === 2) {
            state.isPanning = true;
            state.pinchStartDistance = distanceBetween(e.touches[0], e.touches[1]);
            state.startScale = scale;
            state.startPosition = position;
            state.lastPoint = midpoint(e.touches[0], e.touches[1]);
        }
    };

    const handleTouchMove = (e) => {
        const state = gestureStateRef.current;
        const container = editorContainerRef.current;
        if (!container) return;

        if (e.touches.length === 1 && state.isPanning && state.lastPoint) {
            const current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            const dx = current.x - state.lastPoint.x;
            const dy = current.y - state.lastPoint.y;

            setPosition(prev => clampPosition({
                x: prev.x - dx / (EDITOR_SIZE * PAN_DAMPING),
                y: prev.y - dy / (EDITOR_SIZE * PAN_DAMPING)
            }));

            state.lastPoint = current;
        }

        if (e.touches.length === 2) {
            const newDist = distanceBetween(e.touches[0], e.touches[1]);
            if (state.pinchStartDistance) {
                const ratio = newDist / state.pinchStartDistance;
                const softened = Math.pow(ratio, PINCH_EXPONENT);
                setScale(clamp(state.startScale * softened, 1, 3));
            }
            const mid = midpoint(e.touches[0], e.touches[1]);
            const dx = mid.x - state.lastPoint.x;
            const dy = mid.y - state.lastPoint.y;
            setPosition(prev => clampPosition({
                x: prev.x - dx / (EDITOR_SIZE * PAN_DAMPING),
                y: prev.y - dy / (EDITOR_SIZE * PAN_DAMPING)
            }));
            state.lastPoint = mid;
        }
    };

    const handleTouchEnd = () => {
        gestureStateRef.current = {
            isPanning: false,
            lastPoint: null,
            pinchStartDistance: null,
            startScale: 1,
            startPosition: position
        };
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!isAllowedProfileImageFile(file)) {
                notifyValidationToast('Nur JPG, PNG und JPEG sind erlaubt');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            setFormData({
                ...formData,
                profilePicture: file
            });
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
                // Open modal after image is loaded
                setOpen(true);
            };
            reader.onerror = () => {
                console.error('Error reading file');
                notifyErrorToast('Fehler beim Lesen der Datei', { title: 'Datei konnte nicht gelesen werden' });
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const closeModal = () => {
        setOpen(false);
        setPreviewImage(null);
        setFormData({ profilePicture: null });
        setScale(1);
        setPosition({ x: 0.5, y: 0.5 });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSave = () => {
        if (!formData.profilePicture) return;

        // Validate file type
        if (!isAllowedProfileImageFile(formData.profilePicture)) {
            notifyValidationToast('Nur JPG, PNG und JPEG sind erlaubt');
            return;
        }

        if (editorRef.current) {
            const canvas = editorRef.current.getImageScaledToCanvas();

            try {
                const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
                
                canvas.toBlob((blob) => {
                    if (!blob) {
                        console.error('Failed to create image blob');
                        return;
                    }
                    const editedFile = new File(
                        [blob],
                        (formData.profilePicture?.name?.split('.').slice(0, -1).join('.') || 'profile') + '.jpg',
                        { type: 'image/jpeg' }
                    );

                    const profilePictureData = {
                        file: editedFile,
                        url: dataUrl,
                        fileName: editedFile.name,
                        type: editedFile.type
                    };

                    updateServiceData('profilePicture', profilePictureData);
                    // Clear validation error when profile picture is set
                    setErrors(prev => ({ ...prev, profilePicture: '' }));
                    closeModal();
                }, 'image/jpeg', 0.92);
            } catch (e) {
                console.warn('Preview creation failed:', e);
            }
            return;
        }
        
        // Fallback: if no editor, use original file
        const reader = new FileReader();
        reader.onloadend = () => {
            const profilePictureData = {
                file: formData.profilePicture,
                url: reader.result,
                fileName: formData.profilePicture.name,
                type: formData.profilePicture.type
            };
            updateServiceData('profilePicture', profilePictureData);
            // Clear validation error when profile picture is set
            setErrors(prev => ({ ...prev, profilePicture: '' }));
            closeModal();
        };
        reader.readAsDataURL(formData.profilePicture);
    };

    const handleImageClick = () => {
        // Just trigger the file input, modal will open after file selection
        fileInputRef.current?.click();
    };

    const handleDisplayNameChange = (e) => {
        const value = e.target.value;
        
        // Limit to 40 characters
        if (value.length > 40) {
            return;
        }

        // Validate format (only checks for invalid characters, not empty)
        const formatError = validateDisplayName(value);
        setDisplayNameError(formatError);

        // Clear validation error when user types valid characters
        if (errors.displayName && !formatError && value.trim()) {
            setErrors(prev => ({ ...prev, displayName: '' }));
        }

        // Always update serviceData (even if empty, so validation knows user cleared the field)
        updateServiceData('displayName', value);
    };

    const handleDisplayNameBlur = () => {
        // Validate on blur - show error if field is empty
        const value = serviceData?.displayName?.trim() || '';
        if (!value) {
            setErrors(prev => ({ ...prev, displayName: "Du musst einen Namen eingeben." }));
        }
    };

    const handleJobTitleChange = (e) => {
        const value = e.target.value;

        if (value && !/^[a-zA-ZäöüÄÖÜß\s'.,-]+$/.test(value)) {
            setErrors(prev => ({ ...prev, jobTitle: 'Nummern und Symbole sind nicht erlaubt' }));
            return;
        }
        

        if (value.length > 40) {
            setErrors(prev => ({ ...prev, jobTitle: 'Maximal 40 Zeichen erlaubt' }));
            return;
        }

        // Limit to 40 characters
        if (value.length <= 40) {
            updateServiceData('jobTitle', value);
            // Clear validation error when user types
            if (errors.jobTitle) {
                setErrors(prev => ({ ...prev, jobTitle: '' }));
            }
        }
    };

    return (
        <div className={classes.addUserInfosWrapper}>
            <h2 className="titleSemibold">Erstelle dein Kreativ-Profil</h2>
            <div className={classes.addUserInfosContainer}>
                <div className={classes.addUserInfosInputs}>
                    <div className={classes.addUserInfosInput}>
                        <label className="semiboldMiddle">Profilbild</label>
                        <div className={classes.addUserInfosInputImage} onClick={handleImageClick}>
                            <input 
                                ref={fileInputRef}
                                type="file" 
                                accept="image/*"
                                onChange={handleFileChange}
                                className={classes.addUserInfosInput}
                                style={{ display: 'none' }}
                            />
                            <div className={classes.addUserInfosImageContainer}>
                                {displayProfilePicture && (
                                    <img src={displayProfilePicture} alt="User" />
                                )}
                            </div>
                            <span className="slimRegularLarge">Profilbild ändern</span>
                        </div>
                    </div>
                    <div className={classes.addUserInfosInput}>
                        <label className="semiboldMiddle">Gezeigter Name</label>
                        <div className={classes.addUserInfosInputTextContainer}>
                            <input 
                                type="text" 
                                className={`${classes.addUserInfosInputText} regularMiddle ${(displayNameError || errors.displayName) ? classes.inputError : ''}`} 
                                placeholder="Name" 
                                value={serviceData?.displayName || ''} 
                                onChange={handleDisplayNameChange}
                                onBlur={handleDisplayNameBlur}
                                maxLength={40}
                            />
                            <div className={classes.inputFooter}>
                                {(errors.displayName || displayNameError) && (
                                    <ErrorMessage>{errors.displayName || displayNameError}</ErrorMessage>
                                )}
                                <div className={`${classes.textCounter} slimRegularSmallText`}>
                                    {(serviceData?.displayName?.length || 0)} / 40
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={classes.addUserInfosInput}>
                        <label className="semiboldMiddle">Jobtitel</label>
                        <div className={classes.addUserInfosInputTextContainer}>
                            <textarea 
                                className={`${classes.addUserInfosTextarea} regularMiddle ${errors.jobTitle ? classes.inputError : ''}`} 
                                placeholder="Beispiel: Videograf, Fotograf, Producer"
                                value={serviceData?.jobTitle || ''} 
                                onChange={handleJobTitleChange}
                                maxLength={40}
                            />
                            <div className={classes.inputFooter}>
                                {errors.jobTitle && (
                                    <ErrorMessage>{errors.jobTitle}</ErrorMessage>
                                )}
                                <div className={`${classes.textCounter} slimRegularSmallText`}>
                                    {(serviceData?.jobTitle?.length || 0)} / 40
                                </div>
                            </div>
                        </div>
                    </div>
                    {errors.profilePicture && (
                        <ErrorMessage>{errors.profilePicture}</ErrorMessage>
                    )}
                </div>
                <div className={classes.addUserInfosOverview}>
                    <div className={classes.userFotoContainer}>
                        <div className={classes.userFotoInnerContainer}>
                            <img src={serviceData?.profilePicture?.url || noUserImage} alt="user" />
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="31" height="31" viewBox="0 0 31 31" fill="none">
                            <circle cx="15.8496" cy="15.4727" r="15" fill="white"/>
                            <path d="M15.9805 3.10156C9.07686 3.10156 3.48047 8.69795 3.48047 15.6016C3.48047 22.5052 9.07686 28.1016 15.9805 28.1016C22.8841 28.1016 28.4805 22.5052 28.4805 15.6016C28.4805 8.69795 22.8841 3.10156 15.9805 3.10156ZM22.4198 13.3184C21.4705 14.4369 20.4957 15.5335 19.5304 16.6386C18.5949 17.7102 17.6584 18.7813 16.7208 19.8514C16.5984 19.9912 16.474 20.129 16.3426 20.2603C15.7353 20.8666 14.8232 20.9496 14.1451 20.4172C12.7268 19.3036 11.3142 18.182 9.92086 17.0375C9.00985 16.2892 8.99177 15.1447 9.83577 14.3938C10.1352 14.1268 10.4915 14.0226 10.9021 14.0337C11.3642 14.0141 11.7647 14.2023 12.1269 14.4922C12.9209 15.1272 13.7144 15.7632 14.5089 16.3966C14.8429 16.6631 15.0466 16.6418 15.3253 16.3232C16.7511 14.6937 18.1758 13.0637 19.6043 11.4363C19.7958 11.2172 19.984 10.9906 20.2388 10.8385C20.9552 10.411 21.8215 10.5631 22.3964 11.2161C22.9027 11.7905 22.9341 12.7122 22.4198 13.3184Z" fill="#DF315A"/>
                        </svg>
                    </div>
                    <div className={classes.addUserInfosOverviewItem}>
                        <h3 className="titleSemibold2">{serviceData?.displayName}</h3>
                        {serviceData?.jobTitle ? (
                            <span className="regularMiddle">{serviceData?.jobTitle}</span>
                        ) : (
                            <div className={classes.addUserInfosOverviewItemPlaceholder}></div>
                        )}
                    </div>
                </div>
            </div>

            <SmallModal
                open={open}
                onBackdropClick={closeModal}
                fromCreateService={true}
            >
                <div className={classes.modalContainer}>
                    <header className={classes.modalHeader}>
                        <h2 className='titleSemibold3'>Vorschau deines Profilbilds.</h2>
                        <p className='regularXxs' style={{color: 'var(--dark-grey-4)'}}>
                            Dein Gesicht sollte sichtbar sein – das schafft Vertrauen und Persönlichkeit. Ideal: JPG/PNG, mind. 300×300 px.
                        </p>
                    </header>
                    <div className={classes.modalContent}>
                        {previewImage && (
                            <div className={classes.previewContainer}>
                                <div
                                    className={classes.previewImageContainer}
                                    ref={editorContainerRef}
                                    onWheel={handleWheel}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                    onTouchCancel={handleTouchEnd}
                                >
                                    <AvatarEditor
                                        ref={editorRef}
                                        image={previewImage}
                                        width={200}
                                        height={200}
                                        border={0}
                                        borderRadius={200}
                                        color={[255, 255, 255, 0]}
                                        scale={scale}
                                        rotate={0}
                                        position={position}
                                        onPositionChange={setPosition}
                                        style={{ width: '200px', height: '200px', borderRadius: '50%' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <footer className={classes.modalFooter}>
                        <button onClick={closeModal} className={`${classes.cancelButton} mediumLarge`}>Abbrechen</button>
                        <button 
                            onClick={handleSave} 
                            className={`${classes.saveButton} mediumLarge`}
                            disabled={!formData.profilePicture}
                        >
                            Speichern
                        </button>
                    </footer>
                </div>
            </SmallModal>
        </div>
    );
};

export default AddUserInfos;
